import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { Badge, BottomNavigation, BottomNavigationAction, Box, Paper } from '@mui/material';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { mobileFooterStyles } from '../config';
import { DrawerItem } from '../types';

interface MobileFooterProps {
	drawerItems: DrawerItem[];
	isFarmerPath: boolean;
}

export const MobileFooter: React.FC<MobileFooterProps> = ({ drawerItems, isFarmerPath }) => {
	const { navigateWithLanguage } = useLanguageNavigate();
	const location = useLocation();
	
	// Filter out items marked as hidden from mobile navigation
	const itemsToShow = isFarmerPath 
		? drawerItems.filter(item => !item.hideFromMobile) // Explicit mobile visibility control
		: drawerItems.slice(0, 3);
	
	const currentPath = location.pathname;

	// Helper function to render icon with badge
	const renderIconWithBadge = (item: DrawerItem) => {
		const icon = item.icon;
		if (item.badge?.show && item.badge.count && item.badge.count > 0) {
			return (
				<Badge
					badgeContent={item.badge.count > 99 ? '99+' : item.badge.count}
					color={item.badge.color || 'error'}
					sx={{
						'& .MuiBadge-badge': {
							fontSize: '9px',
							minWidth: '14px',
							height: '14px',
						},
					}}
				>
					{icon}
				</Badge>
			);
		}
		return icon;
	};

	const handleNavigationChange = (_event: React.SyntheticEvent, newValue: number) => {
		if (itemsToShow[newValue]) {
			if (itemsToShow[newValue].onClick) {
				itemsToShow[newValue].onClick?.();
			} else {
				navigateWithLanguage(itemsToShow[newValue].link);
			}
		}
	};

	// Handle touch events for better iOS compatibility
	const handleTouchStart = (event: React.TouchEvent) => {
		// Prevent default to avoid iOS Safari's 300ms delay
		event.preventDefault();
	};

	const handleItemClick = (item: DrawerItem, index: number) => {
		// Direct click handler for better iOS compatibility
		if (item.onClick) {
			item.onClick();
		} else {
			navigateWithLanguage(item.link);
		}
	};

	return (
		<Paper sx={mobileFooterStyles.paper} elevation={3} className="duration-100 translate-y-0">
			<BottomNavigation
				sx={mobileFooterStyles.navigation}
				showLabels
				value={itemsToShow.findIndex((item) => currentPath.includes(item.link))}
				onChange={handleNavigationChange}
			>
				{itemsToShow.map((item, index) => {
					const isActive = currentPath.includes(item.link);
					const isMiddle = isFarmerPath && index === Math.floor(itemsToShow.length / 2);

					return (
						<BottomNavigationAction
							key={item.id}
							label={item.title}
							icon={<Box>{renderIconWithBadge(item)}</Box>}
							onClick={() => handleItemClick(item, index)}
							onTouchStart={handleTouchStart}
							sx={{
								...mobileFooterStyles.action.default,
								...(isActive && mobileFooterStyles.action.active),
								...(isMiddle && mobileFooterStyles.action.middle),
								...(isMiddle && isActive && mobileFooterStyles.action.middleActive),
								// iOS-specific fixes
								cursor: 'pointer',
								WebkitTapHighlightColor: 'transparent',
								WebkitTouchCallout: 'none',
								WebkitUserSelect: 'none',
								userSelect: 'none',
								// Ensure proper touch target size for iOS
								minHeight: '44px',
								minWidth: '44px',
							}}
						/>
					);
				})}
			</BottomNavigation>
		</Paper>
	);
};
